import React, { useRef } from 'react';

import { Box, FormControl, Input, Text } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';

import { IVehicleDescriptionProps } from './types';

const DescriptionInput = Input.withConfig({
  width: 'full',
  borderWidth: 1,
  borderColor: Styles.color.grey.three,
  borderStyle: 'solid',
  borderTopLeftRadius: Styles.borderRadius,
  borderTopRightRadius: Styles.borderRadius,
  borderBottomRightRadius: Styles.borderRadius,
  borderBottomLeftRadius: Styles.borderRadius,

  style: {
    shadowColor: Styles.shadowColor,
    shadowOffset: {
      width: 0,
      height: 0.5,
    },
    shadowRadius: 1.5,
    shadowOpacity: 1,
  },
  color: primitive.bk.$bbqBrown,
  fontFamily: 'body',

  fontSize: 'md',
  marginBottom: '$2',
  placeholderTextColor: Styles.color.grey.four,
});

const ErrorMessage = Text.withConfig({
  color: Styles.color.error,
  margin: 0,
  fontSize: 'xs',
});

const Legend = Box.withConfig({
  _text: {
    fontSize: 'xl',
    fontWeight: 'bold',
  },

  marginTop: 0,
  marginRight: '$2',
  marginLeft: 0,
});

const TitleContainer = Box.withConfig({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  marginBottom: '$2',
});

const TitleAsterisk = Text.withConfig({
  color: Styles.color.error,
  fontSize: '2xl',
});

const DescriptionContainer = FormControl.withConfig({
  borderWidth: 0,
  borderColor: 'black',
  borderStyle: 'solid',
  marginTop: '$4',
  marginBottom: 0,
});

const DescriptionHelp = Text.withConfig({
  fontSize: 'xs',
});

export const VehicleDescriptionMobilePickup: React.FC<React.PropsWithChildren<
  IVehicleDescriptionProps
>> = ({ onChange, value, hasError }) => {
  const { formatMessage } = useIntl();
  const curbsideVehicleDescriptionMaxLength = useFlag(
    LaunchDarklyFlag.CURBSIDE_VEHICLE_DESCRIPTION_MAX_LENGTH
  );

  const errorMessage = formatMessage(
    { id: 'descriptionLengthError' },
    {
      min: 1,
      max: curbsideVehicleDescriptionMaxLength,
    }
  );

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (description: string) => {
    const value = description?.trimStart();
    const isValid = curbsideVehicleDescriptionMaxLength
      ? value && value.length <= curbsideVehicleDescriptionMaxLength
      : true;
    onChange({ value, hasError: !isValid });
  };

  return (
    <DescriptionContainer>
      <TitleContainer>
        <Legend>{formatMessage({ id: 'mobilePickupVehicleTitle' })}</Legend>
        <Text>({formatMessage({ id: 'required' })})</Text>
        <TitleAsterisk>*</TitleAsterisk>
      </TitleContainer>
      <DescriptionInput
        testID="vehicle-description"
        placeholder={formatMessage({ id: 'mobilePickupVehicleDescriptionPlaceholder' })}
        onChangeText={handleChange}
        value={value}
        ref={inputRef}
      />
      {hasError ? (
        <ErrorMessage>{errorMessage}</ErrorMessage>
      ) : (
        <DescriptionHelp>{formatMessage({ id: 'mobilePickupVehicleDescription' })}</DescriptionHelp>
      )}
    </DescriptionContainer>
  );
};
